import { Order, OrderEvent } from 'store/orders/orders.types'
import {
  DROP_EQUIPMENT_SEQUENCE_BOUND,
  getEquipmentHookIds,
  getMaxMiddleSequenceId,
} from 'store/trips/trips.utils'
import {
  CreateTripPayload,
  EventType,
  TripPayload,
  TripType,
} from 'store/trips/trips.types'

export const addEquipment = (
  eventType: EventType | TripType,
  { tripTasks }: CreateTripPayload,
  orderEvent: OrderEvent,
  order?: Order,
  userOrderId?: string,
  targetTrailerId?: string | null
): TripPayload[] => {
  const equipmentHookTrailer = [...tripTasks]
    .filter(
      (task) =>
        task.type === 'HOOK_TRAILER' &&
        task.orderEventId == null &&
        task.sourceOrderPkId == null &&
        !task.orderId
    )
    .sort((a, b) => b.sequenceId - a.sequenceId)[0]

  const isPickupLikeTask = (task: TripPayload) =>
    task.type === 'PICKUP' ||
    (task.type === 'HOOK_TRAILER' &&
      (task.orderEventId != null || task.sourceOrderPkId != null))

  const maxPickupSequenceId = tripTasks
    .filter(isPickupLikeTask)
    .reduce((max, task) => Math.max(max, task.sequenceId), -Infinity)

  const { tractorId, trailerId: currentTrailerId } =
    getEquipmentHookIds(tripTasks)
  const trailerId =
    targetTrailerId === undefined ? currentTrailerId : targetTrailerId

  const maxMiddleSequenceId = getMaxMiddleSequenceId(tripTasks)
  const lastMiddleTask = [...tripTasks]
    .filter((task) => task.sequenceId < DROP_EQUIPMENT_SEQUENCE_BOUND)
    .sort((a, b) => a.sequenceId - b.sequenceId)
    .at(-1)

  const orderUserId = order?.userOrderId ?? userOrderId
  const isSameOrderTask = (task: TripPayload) =>
    (order?.orderId != null && task.sourceOrderPkId === order.orderId) ||
    (orderUserId != null && task.orderId === orderUserId)

  let sequenceId: number
  if (eventType === 'PICKUP' || eventType === 'HOOK_TRAILER') {
    const sameOrderDelivery = tripTasks.find(
      (task) => task.type === 'DELIVERY' && isSameOrderTask(task)
    )

    if (sameOrderDelivery) {
      // Insert before same-order delivery (normalize ranks pickup-like above it)
      sequenceId = sameOrderDelivery.sequenceId
    } else if (maxPickupSequenceId === -Infinity) {
      if (equipmentHookTrailer) {
        sequenceId = equipmentHookTrailer.sequenceId + 1
      } else {
        sequenceId =
          maxMiddleSequenceId === -Infinity ? 1 : maxMiddleSequenceId + 1
      }
    } else if (lastMiddleTask?.type === 'DELIVERY') {
      sequenceId = maxMiddleSequenceId + 1
    } else {
      sequenceId = maxPickupSequenceId + 1
    }
  } else {
    const sameOrderPickupLike = [...tripTasks]
      .filter((task) => isPickupLikeTask(task) && isSameOrderTask(task))
      .sort((a, b) => b.sequenceId - a.sequenceId)[0]

    if (sameOrderPickupLike) {
      sequenceId = sameOrderPickupLike.sequenceId + 1
    } else {
      sequenceId =
        maxPickupSequenceId === -Infinity
          ? maxMiddleSequenceId === -Infinity
            ? 1
            : maxMiddleSequenceId + 1
          : maxPickupSequenceId + 1
    }
  }

  return [
    {
      orderEventId: orderEvent.orderEventId,
      type: eventType,
      sequenceId,
      tractorId,
      trailerId,
      name: orderEvent.name,
      address: orderEvent.address,
      startTime: orderEvent.startTime,
      estimatedTime: orderEvent.endTime,
      orderId: order?.userOrderId ?? userOrderId,
      sourceOrderPkId: order?.orderId,
    },
  ]
}
